import React, { useContext, useState } from 'react'
import classNames from 'classnames'
import { startCase } from 'lodash'

import CurrentLocaleContext from 'marketing-site/src/components/context/CurrentLocaleContext'
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect'
import { containerMaxWidth, getColorTheme, getHexColorFromName, mq } from '../../utils'
import { Text } from '../../elements/Text'
import { VisuallyHidden } from '../../elements/VisuallyHidden'
import { Statistic } from '../../elements/Statistic'
import { IProps } from './index'

export const Statistics: React.FC<IProps> = ({
  heading,
  statistics,
  backgroundColor = 'transparent',
  variation = 'standard',
  footer,
}: IProps) => {
  const [shouldLocalise, setShouldLocalise] = useState<boolean>(false)
  const { default: isDefaultLocale } = useContext(CurrentLocaleContext)

  const themeColors = getColorTheme('default', getHexColorFromName(startCase(backgroundColor)))
  const statisticsClasses = classNames('statistics', {
    [`statistics--${variation}`]: variation,
    [`background-color-${backgroundColor}`]: variation === 'standard',
    'background-color-transparent': variation !== 'standard',
  })

  useIsomorphicLayoutEffect(() => {
    !isDefaultLocale && setShouldLocalise(!isDefaultLocale)
  }, [isDefaultLocale])

  return (
    <div className={statisticsClasses}>
      <VisuallyHidden>
        <Text brand="default" size="heading-2">
          {heading}
        </Text>
      </VisuallyHidden>
      <ul>
        {statistics.map((stat) => {
          return (
            <li key={stat.stat}>
              <Statistic
                {...stat}
                parentBackgroundColor={themeColors.backgroundColor}
                parentVariation={variation}
                shouldLocalise={shouldLocalise}
              />
            </li>
          )
        })}
      </ul>
      {footer && (
        <div className="statistics__footer">
          <Text brand="default" size="caption">
            {footer}
          </Text>
        </div>
      )}
      <style jsx>
        {`
          .statistics {
            ul {
              display: grid;
              grid-row-gap: 0.5rem;
              grid-column-gap: 1.5rem;
            }
          }

          .statistics__footer {
            text-align: center;
            grid-column: content;
            grid-row: 2;
          }

          .statistics--standard {
            position: relative;
            background-color: ${themeColors.backgroundColor};

            &--cards,
            &--splitted-cards {
              display: block;
              width: 85vw;
              margin: 0 auto;
              background: none;
            }
          }

          @media (${mq.tablet}) {
            .statistics {
              display: grid;

              ul {
                grid-template-columns: repeat(4, 1fr);
                grid-gap: 2rem;
              }

              li {
                grid-column: span 2;
              }

              &--standard {
                grid-template-columns: 1fr [content] 12fr 1fr;

                ul {
                  grid-column: content;
                  grid-column-gap: 1rem;
                  grid-template-columns: repeat(4, minmax(auto, 8.75rem));
                  justify-items: center;
                  margin: 0 auto;
                }

                li {
                  &:first-child:not(:last-child) {
                    justify-self: end;
                  }

                  &:nth-child(2) {
                    justify-self: start;
                  }

                  &:nth-child(3n) {
                    grid-column: 2 / span 2;
                  }
                }
              }

              &--cards,
              &--splitted-cards {
                li {
                  &:nth-child(3n - 2):first-child {
                    grid-column: span 4;
                  }

                  &:nth-last-child(2):nth-child(3n - 2) {
                    grid-column: span 2;
                  }
                }
              }
            }
          }

          @media (${mq.laptop}) {
            .statistics {
              ul {
                grid-template-columns: repeat(6, 1fr);
                grid-gap: 4rem;
              }

              li {
                &:nth-child(3n - 2):last-child {
                  grid-column: 3 / span 2;
                }
              }

              &--standard {
                ul {
                  grid-template-columns: repeat(6, minmax(auto, 8.75rem));
                  grid-column-gap: 1rem;
                }

                li {
                  max-width: none;

                  &:nth-last-child(2):nth-child(3n - 2) {
                    grid-column: 2 / span 2;
                  }

                  &:nth-child(3n - 1):not(:last-child) {
                    justify-self: center;
                  }

                  &:nth-child(3n) {
                    grid-column: span 2;
                  }
                }
              }

              &--cards,
              &--splitted-cards {
                max-width: ${containerMaxWidth};
                grid-template-columns: 0fr [content] 1fr 0fr;
                margin: 0 auto;

                ul {
                  grid-column: content;
                }

                li {
                  &:nth-child(3n - 2):first-child {
                    grid-column: span 2;
                  }

                  &:nth-last-child(2):nth-child(3n - 2) {
                    grid-column: 2 / span 2;
                  }

                  &:nth-child(3n - 2):last-child {
                    grid-column: 3 / span 2;
                  }
                }
              }
            }
          }
        `}
      </style>
    </div>
  )
}
